import { TeamWith } from "@redotech/account-common/base-team";
import {
  InternationalPriceRoundingSettings,
  ReturnsSettings,
} from "@redotech/redo-return-settings-common/returns-settings";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { memo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, ButtonBorder, ButtonSize, ButtonTheme } from "../button";

export const internationalPriceRoundingForm = groupInput({
  enabled: input<boolean>(),
  lowerPriceEnding: input<string>(),
  upperPriceEnding: input<string>(),
  excludedCatalogIds: input<string[]>(),
});

export type InternationalPriceRoundingForm = InputSchema.Input<
  typeof internationalPriceRoundingForm
>;

export type InternationalPriceRoundingValue = InputSchema.Value<
  typeof internationalPriceRoundingForm
>;

export const internationalPriceRoundingDefault: InternationalPriceRoundingValue =
  {
    enabled: false,
    lowerPriceEnding: "0.48",
    upperPriceEnding: "0.98",
    excludedCatalogIds: [],
  };

export function internationalPriceRoundingTeamToFormValue(
  team: TeamWith<{ returns?: ReturnsSettings }>,
): InternationalPriceRoundingValue {
  const settings = team.settings?.returns?.internationalPriceRounding;
  if (!settings) {
    return internationalPriceRoundingDefault;
  }
  return {
    enabled: settings.enabled,
    lowerPriceEnding: settings.lowerPriceEnding.toFixed(2),
    upperPriceEnding: settings.upperPriceEnding.toFixed(2),
    excludedCatalogIds: settings.excludedCatalogIds ?? [],
  };
}

export function internationalPriceRoundingFormValueToTeam(
  value: InternationalPriceRoundingValue,
): InternationalPriceRoundingSettings {
  const lower = parseFloat(value.lowerPriceEnding);
  const upper = parseFloat(value.upperPriceEnding);
  return {
    enabled: value.enabled,
    lowerPriceEnding: Number.isNaN(lower) ? 0.48 : lower,
    upperPriceEnding: Number.isNaN(upper) ? 0.98 : upper,
    excludedCatalogIds: value.excludedCatalogIds,
  };
}

export const InternationalPriceRoundingButton = memo(
  function InternationalPriceRoundingButton() {
    const navigate = useNavigate();
    return (
      <Button
        border={ButtonBorder.LIGHT}
        fullWidth
        onClick={() => navigate("../international-price-rounding")}
        size={ButtonSize.EXTRA_SMALL}
        theme={ButtonTheme.OUTLINED}
      >
        Configure International Price Rounding
      </Button>
    );
  },
);
